import React, { useRef, useState } from "react";

// Allowed video extensions
const ALLOWED_VIDEO_EXTENSIONS = [".mp4", ".mov", ".webm"];
const VIDEO_MAX_SIZE = 10 * 1024 * 1024; // 10MB
const IMAGE_PDF_MAX_SIZE = 5 * 1024 * 1024; // 5MB

// Check if a file is a video by MIME type or extension
const isVideoFile = (file) => {
  if (file.type && file.type.startsWith("video/")) return true;
  const ext = "." + file.name.split(".").pop().toLowerCase();
  return ALLOWED_VIDEO_EXTENSIONS.includes(ext);
};

const UploadInputButton = ({
  label,
  id,
  note = "Allowed: PNG, JPEG, PDF (max 5MB) | MP4, MOV, WebM (max 10MB)",
  placeholder = "Choose a file",
  className = "",
  labelClassName = "",
  spanClassName = "",
  noteClassName = "",
  disabled = false,
  error = "",
  accept = ".pdf, image/png, image/jpeg, video/mp4, video/quicktime, video/webm, .mp4, .mov, .webm",
  onChange,
  multiple = false,
  hideLabels = false,
  hideNote = false, // New prop to control note visibility independently
}) => {
  const fileInputRef = useRef(null);
  const [selectedFileNames, setSelectedFileNames] = useState(""); // Changed to handle multiple files
  const [validationError, setValidationError] = useState("");

  const handleButtonClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      // Validate each file for type and size
      for (const file of files) {
        if (isVideoFile(file)) {
          const ext = "." + file.name.split(".").pop().toLowerCase();
          if (!ALLOWED_VIDEO_EXTENSIONS.includes(ext)) {
            setValidationError(
              "Unsupported video format. Only MP4, MOV and WebM are allowed."
            );
            if (fileInputRef.current) fileInputRef.current.value = "";
            setSelectedFileNames("");
            if (onChange) onChange(multiple ? [] : "");
            return;
          }
          if (file.size > VIDEO_MAX_SIZE) {
            setValidationError(
              `Video "${file.name}" exceeds the 10MB limit.`
            );
            if (fileInputRef.current) fileInputRef.current.value = "";
            setSelectedFileNames("");
            if (onChange) onChange(multiple ? [] : "");
            return;
          }
        } else {
          if (file.size > IMAGE_PDF_MAX_SIZE) {
            setValidationError(
              `File "${file.name}" exceeds the 5MB limit.`
            );
            if (fileInputRef.current) fileInputRef.current.value = "";
            setSelectedFileNames("");
            if (onChange) onChange(multiple ? [] : "");
            return;
          }
        }
      }

      // All files passed validation
      setValidationError("");

      if (multiple) {
        const fileNames = files.map((file) => file.name).join(", ");
        setSelectedFileNames(fileNames);
        if (onChange) {
          onChange(files);
        }
      } else {
        setSelectedFileNames(files[0]?.name || "");
        if (onChange) {
          onChange(files[0] || "");
        }
      }
    }
  };

  // Split note into title and content
  const [noteTitle, noteContent] = note.split(":");

  return (
    <div className="w-full flex flex-col gap-1">
      {!hideLabels && label && (
        <label
          htmlFor={id}
          className={`text-subtitle2 font-ptsans text-secondary/90 font-medium pl-2 block ${labelClassName}`}
        >
          {label}
        </label>
      )}
      <div
        className={`flex ${
          hideLabels ? "flex-col" : "flex-row"
        } gap-2 items-center`}
      >
        <button
          type="button"
          onClick={handleButtonClick}
          className={`h-9 w-20 px-2 text-background rounded-xl text-body2 font-ptsans focus:outline-none focus:ring-2 ${
            disabled
              ? "bg-secondary/35 cursor-not-allowed"
              : "bg-secondary/90 hover:scale-[1.03] hover:bg-secondary/95 focus:ring-primary/60 transition-all ease-in-out duration-100"
          } ${className}`}
          disabled={disabled}
        >
          Upload
        </button>
        <span
          className={`text-caption md:text-body2 pr-2 font-ptsans text-secondary/60 line-clamp-2 ${spanClassName}`}
        >
          {selectedFileNames || placeholder}
        </span>
      </div>
      <input
        type="file"
        id={id}
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={accept}
        style={{ display: "none" }}
        disabled={disabled}
        multiple={multiple}
      />
      {!hideLabels && !hideNote && note && (
        <span
          className={`text-sm mt-1 ml-2 ${
            disabled ? "text-secondary/35" : "text-secondary/90"
          } ${noteClassName}`}
        >
          <span className={`font-bold text-body1 font-ptsans ${noteClassName}`}>
            {noteTitle}:
          </span>
          <span
            className={`text-secondary/70 text-caption md:text-body2 font-ptsans ${noteClassName}`}
          >
            {noteContent}
          </span>
        </span>
      )}
      {validationError && (
        <span className="text-caption text-red-500 font-ptsans pl-2 mt-1">
          {validationError}
        </span>
      )}
      {!hideLabels && error && (
        <span className="text-caption text-red-500 font-ptsans pl-2">
          {error}
        </span>
      )}
    </div>
  );
};

export default UploadInputButton;

